<script setup>
import { ref } from 'vue'
import LoadingButton from '@/Shared/LoadingButton.vue'
import TabLayout from '@/Shared/TabLayout.vue'
import SecurityIcon from '@/Shared/Icon/SecurityIcon.vue'
import UserIcon from '@/Shared/Icon/User.vue'
import { useForm } from '@inertiajs/vue3'
import Icon from '@/Shared/Icon.vue'
import TextInput from '@/Shared/TextInput.vue'

const props = defineProps({
  user: {
    type: Object,
    required: true
  }
})

const form = useForm({
  name: props.user.name,
  email: props.user.email
})

const activeTab = ref('profile')

const tabs = [
  { id: 'profile', label: 'Perfil', icon: UserIcon },
  { id: 'security', label: 'Seguridad', icon: SecurityIcon }
]

const breadcrumbs = [{ label: 'Mi Perfil' }]

const submit = () => {
  form.put('/profile')
}

const handleTabChange = (tabId) => {
  activeTab.value = tabId
}
</script>

<template>
  <TabLayout
    title="Mi Perfil"
    :breadcrumbs="breadcrumbs"
    :tabs="tabs"
    default-active-tab="profile"
    @tab-change="handleTabChange"
  >
    <template #default="{ activeTab: currentTab }">
      <!-- Panel de Perfil -->
      <div
        v-if="currentTab === 'profile'"
        class="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden"
      >
        <form @submit.prevent="submit">
          <div class="p-6 border-b border-gray-200">
            <h2 class="text-xl font-medium text-gray-800">Información personal</h2>
            <p class="mt-1 text-sm text-gray-500">
              Actualiza tu información personal y de contacto.
            </p>
          </div>

          <div class="p-6 space-y-6">
            <!-- Avatar y nombre -->
            <div class="flex items-center space-x-4">
              <div
                class="w-20 h-20 rounded-full bg-indigo-600 flex items-center justify-center text-2xl font-bold text-white"
              >
                {{ user.name ? user.name.charAt(0).toUpperCase() : 'U' }}
              </div>
              <div>
                <h3 class="text-lg font-medium text-gray-800">{{ user.name }}</h3>
                <p class="text-sm text-gray-500">Miembro desde: {{ user.created_at }}</p>
                <p class="text-sm text-gray-500">Último acceso: {{ user.last_login_at }}</p>
              </div>
            </div>

            <!-- Formulario de información personal -->
            <div class="space-y-4 mt-6">
              <div>
                <TextInput
                  v-model="form.name"
                  label="Nombre"
                  :error="form.errors.name"
                  class="mb-4"
                />
              </div>

              <div>
                <TextInput
                  v-model="form.email"
                  type="email"
                  label="Correo electrónico"
                  :error="form.errors.email"
                  class="mb-4"
                />
              </div>
            </div>
          </div>

          <div class="px-6 py-4 bg-gray-50 border-t border-gray-200 flex justify-end items-center">
            <LoadingButton :loading="form.processing" class="btn-indigo" type="submit">
              Guardar cambios
            </LoadingButton>
          </div>
        </form>
      </div>

      <!-- Panel de Seguridad -->
      <div
        v-else-if="currentTab === 'security'"
        class="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden"
      >
        <div class="p-6 border-b border-gray-200">
          <h2 class="text-xl font-medium text-gray-800">Configuración de seguridad</h2>
          <p class="mt-1 text-sm text-gray-500">Gestiona las opciones de seguridad de tu cuenta.</p>
        </div>

        <div class="p-6 space-y-6">
          <div class="text-center py-8">
            <Icon name="SecurityIcon" class="h-16 w-16 text-gray-400 mx-auto mb-4" />
            <h3 class="text-lg font-medium text-gray-800 mb-2">Cambio de contraseña</h3>
            <p class="text-sm text-gray-600 max-w-md mx-auto">
              Esta sección está actualmente en desarrollo. Pronto podrás cambiar tu contraseña y
              configurar opciones de seguridad adicionales aquí.
            </p>
          </div>
        </div>
      </div>
    </template>
  </TabLayout>
</template>
